import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Query Type master (Configuration → Masters). A simple lookup that classifies
 * leads (e.g. "international_package", "domestic_flight"). See ADR-0016 for the
 * standard simple-master shape this follows.
 */

/** Stable machine code: lowercase letters, digits, underscores. */
export const MasterCodeSchema = z
  .string()
  .trim()
  .min(1, 'Code is required')
  .max(64)
  .regex(/^[a-z0-9_]+$/, 'Use lowercase letters, numbers, and underscores');

export const QueryTypeListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type QueryTypeListItem = z.infer<typeof QueryTypeListItemSchema>;

export const QueryTypeListQuerySchema = PaginationQuerySchema.extend({
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type QueryTypeListQuery = z.infer<typeof QueryTypeListQuerySchema>;

export const QueryTypeListResponseSchema = paginatedResponseSchema(QueryTypeListItemSchema);
export type QueryTypeListResponse = z.infer<typeof QueryTypeListResponseSchema>;

export const QueryTypeDetailSchema = QueryTypeListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type QueryTypeDetail = z.infer<typeof QueryTypeDetailSchema>;

const QueryTypeCoreFieldsSchema = z.object({
  code: MasterCodeSchema,
  name: z.string().trim().min(1, 'Name is required').max(120),
  description: z.string().trim().max(500).nullable().optional(),
  legacyId: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateQueryTypeRequestSchema = QueryTypeCoreFieldsSchema;
export type CreateQueryTypeRequest = z.infer<typeof CreateQueryTypeRequestSchema>;

export const UpdateQueryTypeRequestSchema = QueryTypeCoreFieldsSchema.partial();
export type UpdateQueryTypeRequest = z.infer<typeof UpdateQueryTypeRequestSchema>;
